import BlogTrackedCtaLink from './BlogTrackedCtaLink';
import FunnelEmailPopoverCta from './FunnelEmailPopoverCta';
import { FUNNEL_CTA_SHELL, FUNNEL_SECONDARY } from './FunnelCtaNav';
import {
  DEFAULT_BLOG_CONVERSION_CTA_SUBTITLE,
  DEFAULT_BLOG_CONVERSION_CTA_TITLE,
  type IBlogConversionCtaMountConfig,
} from '../_data/blog-conversion-cta-mount';
import { trackBlogCtaClick } from '@/app/_lib/analytics-events';
import { trackBlogCtaClick as trackBlogCtaLocationClick, type TBlogCtaLocation } from '@/app/_utils/analytics';
import { cn } from '@/app/_utils/cn';

interface IBlogConversionCtaSectionProps {
  config: IBlogConversionCtaMountConfig;
  pageSlug: string;
  analyticsSection?: string;
  /** config.title보다 우선. 생략 시 config.title → 기본 문구 */
  title?: string;
  /** config.lead보다 우선. 생략 시 config.lead */
  lead?: string;
  /** config.subtitle보다 우선. 생략 시 config.subtitle → 기본 문구 */
  subtitle?: string;
  /** top / mid / bottom simple click location */
  location?: TBlogCtaLocation;
}

const BlogConversionCtaSection = ({
  config,
  pageSlug,
  analyticsSection = 'bottom_cta',
  title: titleProp,
  lead: leadProp,
  subtitle: subtitleProp,
  location = 'bottom',
}: IBlogConversionCtaSectionProps) => {
  const sectionId = 'blog-conversion-cta-heading';
  const title = titleProp ?? config.title ?? DEFAULT_BLOG_CONVERSION_CTA_TITLE;
  const lead = leadProp ?? config.lead;
  const subtitle = subtitleProp ?? config.subtitle ?? DEFAULT_BLOG_CONVERSION_CTA_SUBTITLE;
  const trackCtaClick = (ctaName: IBlogConversionCtaMountConfig['actions'][number]['ctaName']) => {
    trackBlogCtaClick({
      cta_name: ctaName,
      page: pageSlug,
      section: analyticsSection,
    });
    trackBlogCtaLocationClick({
      location,
      page: pageSlug,
    });
  };

  const getVisualClassName = (visual: IBlogConversionCtaMountConfig['actions'][number]['visual']) => {
    if (visual === 'primary') {
      return 'blog-conversion-cta-primary blog-conversion-cta-breathe rounded-full border-transparent text-white shadow-none';
    }

    if (visual === 'tertiary') {
      return cn(
        'blog-funnel-ignore rounded-xl border border-border/75 bg-transparent text-muted-foreground shadow-none',
        'hover:border-border hover:text-foreground hover:bg-card/40',
      );
    }

    return FUNNEL_SECONDARY;
  };

  return (
    <section
      className='blog-conversion-cta-react not-prose mt-14 mb-20 scroll-mt-10 md:mt-20 md:mb-24'
      aria-labelledby={sectionId}
      lang='en'
    >
      <div
        className={cn(
          'mx-auto max-w-3xl rounded-2xl border border-border/55 bg-linear-to-b from-card via-card to-[hsl(var(--muted)/0.35)]',
          'px-6 py-9 shadow-[0_12px_40px_-18px_rgba(15,23,42,0.12),inset_0_1px_0_0_rgb(255_255_255/0.65)]',
          'sm:px-10 sm:py-10',
        )}
      >
        <div className='text-center'>
          <h2
            id={sectionId}
            className='text-balance text-[clamp(1.25rem,3.2vw,1.65rem)] font-bold tracking-[-0.03em] text-foreground'
          >
            {title}
          </h2>
          {lead ? <p className='mt-3 text-sm font-semibold text-foreground/75'>{lead}</p> : null}
          <p className='mt-3 mx-auto max-w-xl whitespace-pre-line text-base leading-relaxed text-muted-foreground'>
            {subtitle}
          </p>
        </div>

        <div className='mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:mt-9 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center'>
          {config.actions.map(action => {
            const actionClassName = cn(
              FUNNEL_CTA_SHELL,
              getVisualClassName(action.visual),
              'sm:w-auto',
              action.visual !== 'tertiary' && 'sm:min-w-54',
            );

            if (action.visual === 'tertiary' && action.href.startsWith('mailto:')) {
              const email = action.href.replace('mailto:', '').split('?')[0] ?? '';
              return (
                <FunnelEmailPopoverCta
                  key={`${action.href}-${action.label}`}
                  label={action.label}
                  email={email}
                  className={actionClassName}
                  onTrackedClick={() => trackCtaClick(action.ctaName)}
                />
              );
            }

            return (
              <BlogTrackedCtaLink
                key={`${action.href}-${action.label}`}
                href={action.href}
                pageSlug={pageSlug}
                ctaName={action.ctaName}
                section={analyticsSection}
                location={location}
                className={actionClassName}
              >
                {action.label}
              </BlogTrackedCtaLink>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BlogConversionCtaSection;
